import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MdaePopup, {
  MdaePopupContent,
  MdaePopupFooter,
  MdaePopupFooterEndButtons,
  MdaePopupFooterStartButtons,
  MdaePopupTitle,
} from "../../../../common_components/common_widgets/popup";
import MdaeButton from "../../../../common_components/common_widgets/button";
import MdaeTabPanel from "../../../../common_components/common_widgets/tab-panel";
import { Item } from "devextreme-react/tab-panel";
import DocumentManagement, {
  USER_ACTION_MODE,
  readExcelFromBucket,
} from "../../../../common_components/document-management/component";
import WaitTimeMessageModal from "../../../../common_components/document-management/wait-time-message-modal";
import MdaeFileUploader from "../../../../common_components/common_widgets/file-uploader";
import MdaeDataGrid from "../../../../common_components/common_widgets/data-grid";
import { extractDataFromExcelSheet } from "../../../../utils/excelUtils";
import { Workbook } from "exceljs";
import saveAs from "file-saver";
import { actions as asyncActions } from "async-ops";
import { bindActionCreators } from "redux";
import { first, isEqual } from "lodash";
import Guid from "devextreme/core/guid";
import {
  OutcomeTypes,
  SET_CASE_FORM_DATA,
  SET_UPLOADED_CHARGE_DATA,
  TOGGLE_UPLOAD_DOCUMENT_TARGET_CHARGES_POPUP,
} from "./reducers/case-data-reducer";
import {
  HIDE_LOADER,
  SHOW_LOADER,
} from "../../../../common_components/mdae-common-reducer";
import { VALIDATE_UPLOADED_CHARGE_DATA } from "./services/validate-uploaded-charge-data";
import { SET_DOCUMENT_DATA_TO_SAVE, refreshDocumentManagementGridInstance } from "../../../../common_components/document-management/reducers/document-management-reducer";


export const toggleUploadTargetDataPopup = (dispatch, isVisible) => {
  dispatch({
    type: TOGGLE_UPLOAD_DOCUMENT_TARGET_CHARGES_POPUP,
    data: { isUploadTargetDataPopupVisible: isVisible },
  });
};
export const abortUpload = () => {
  return Promise.resolve();
};

const getUploadLineLevelChargesDataGridConfigs = (
  uploadedChargeData: any[],
  roundTargetTypes
) => {
  const baseColumns = [
    {
      allowSorting: false,
      allowFiltering: false,
      fixed: true,
      allowExporting: false,
      allowResizing: false,
      allowReordering: false,
      alignment: "center",
      width: 50,
      cellRender: (cellData) => {
        const { isValid, error } = cellData.data || {};
        return isValid === false ? (
          <div>
            <i
              className="dx-icon warning-red"
              title={error}
              style={{ height: 18 }}
            />
          </div>
        ) : (
          <div>
            <i
              className="fa fa-check-circle"
              style={{ color: "green", fontSize: "15px" }}
            />
          </div>
        );
      },
    },
    {
      dataField: "roundTargetType",
      caption: "Target Type",
      allowSorting: true,
      calculateDisplayValue: (row) => {
        const value = row.roundTargetType;
        const match = roundTargetTypes?.find((x) => x.value === value);
        return match ? match.displayName : value;
      },
      headerCellRender: () => (
        <span>
          Target Type <span style={{ color: "red" }}>*</span>
        </span>
      ),
    },
    {
      dataField: "roundTargetValue",
      caption: "Target Value",
      allowSorting: true,
      headerCellRender: () => (
        <span>
          Target Value <span style={{ color: "red" }}>*</span>
        </span>
      ),
    },
    {
      dataField: "atRiskAmnt",
      caption: "At Risk $",
      allowSorting: true,
      width: 100,
    },
  ];

  const extraColumns =
  uploadedChargeData?.length > 0
    ? [
        {
          dataField: "error",
          caption: "Error",
          allowSorting: true,
          width: 200,
        },
      ]
    : [];

return {
  id: "grdUploadLineLevelCharges",
  allowColumnResizing: true,
  allowColumnReordering: true,
  mdaeColumns: [...baseColumns, ...extraColumns],
  dataSource: uploadedChargeData,
  allowUpdating: false,
  allowAdding: false,
  allowDeleting: false
};
};


const safeString = (v) =>
  v === null || v === undefined ? "" : String(v).trim();

const findKeyContaining = (obj, substr) => {
  if (!obj) return undefined;
  const lower = substr.toLowerCase();
  return Object.keys(obj).find((k) => k.toLowerCase().includes(lower));
};

const TargetTypeDataGrid  = () => {

  const uploadedChargeData = useSelector(
    (state) => state["caseDataReducer"].uploadedChargeData,
    isEqual
  );
  const roundTargetTypes = useSelector(
    (state) => state["caseDataReducer"].roundTargetTypes
  );
  const uploadGridConfig = useMemo(() => {
    return getUploadLineLevelChargesDataGridConfigs(uploadedChargeData,roundTargetTypes);
  }, [uploadedChargeData,roundTargetTypes]);

  return (
    <div>
      <MdaeDataGrid {...uploadGridConfig} />
    </div>
  );
};
interface IUploadRoundLineLevelTrackingData {
  onRowsLoaded: (rows: any[]) => any;
}
const EditOrAttachDocument : React.FC<IUploadRoundLineLevelTrackingData> = (
  props
) => {
  const dispatch = useDispatch();

  const uploadFileHandler = useCallback(
    async (e) => {
      if (e.value?.length > 0) {
        const file = e.value[0];
        const response: any = await extractDataFromExcelSheet(file, 0);
        const rows = response?.XL_row_object ?? [];
        props.onRowsLoaded(rows);
      }
    },
    [props.onRowsLoaded]
  );

  const downloadTemplate = useCallback(
    (params) => {
      const result = params?.validationGroup
        ? params.validationGroup.validate()
        : null;
      if (!result?.isValid) return;

      dispatch({ type: SHOW_LOADER });

      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Template");
      worksheet.columns = [
        { header: "Target Type (Required)", key: "targetType", width: 25 },
        { header: "Target Value (Required)", key: "targetValue", width: 25 },
        { header: "At Risk $ (optional)", key: "atRiskDollar", width: 20 },
      ];
      worksheet.addRow({ targetType: "", targetValue: "", atRiskDollar: "" });

      workbook.xlsx
        .writeBuffer()
        .then((buffer) => {
          saveAs(
            new Blob([buffer], { type: "application/octet-stream" }),
            "Round Line Level Charges Template.xlsx"
          );
        })
        .finally(() => dispatch({ type: HIDE_LOADER }));
    },
    [dispatch]
  );

  return (
    <>
      <div style={{ display: "flex" }}>
        <MdaeFileUploader
          id={"btnTargetChargesFileUploader"}
          maxFileSize={500000000}
          onValueChanged={uploadFileHandler}
          abortUpload={abortUpload}
          defaultValue={[]}
          width={"80%"}
        />
        <MdaeButton
          id="downloadKeyInfoTemplate"
          role={"dxButton"}
          stylingMode="text"
          className={"text-icon-button"}
          onClick={downloadTemplate}
        >
          <span className={"download-icon"} style={{ height: "18px" }} />
          <span
            className={
              "download-template-title application-section-primary-color"
            }
          >
            Download Template
          </span>
        </MdaeButton>

        <div>
          <label id="file-size-limit" />
        </div>
      </div>
    </>
  );
};

interface IUploadRoundLineLevelTracking {
  eaRoundId: number ; 
  setRoundCharges: React.Dispatch<React.SetStateAction<any[]>>;
  eaCaseId: number ;
  currentEaRoundCaseId: number ;
}
const UploadTargetDataPopup :React.FC<IUploadRoundLineLevelTracking>= ({
  eaRoundId,
  setRoundCharges,
  eaCaseId,
  currentEaRoundCaseId,
}) => {
  const dispatch = useDispatch();
  const asyncAction = bindActionCreators(
    { asyncOperationStart: asyncActions.asyncOperationStart },
    dispatch
  );

  const [rows, setRows] = useState([]);
  const [selectDocument, setSelectedDocument] = useState([]);
  const [fileResetKey, setFileResetKey] = useState(Date.now());

  const isVisible = useSelector(
    (state) => state["caseDataReducer"].isUploadTargetDataPopupVisible
  );
  const uploadedChargeData = useSelector(
    (state) => state["caseDataReducer"]?.uploadedChargeData,
    isEqual
  );
  const doesHaveAnyInValidRow = useSelector((state) => {
    const data = state["caseDataReducer"].uploadedChargeData;
    return Array.isArray(data) && data.some((r) => r.isValid === false);
  }, isEqual);

  const caseData = useSelector((state) => state["caseDataReducer"].caseData);
  
  const auditPropertiesData = useSelector(
    (state) =>  state["auditPropertiesReducer"].auditPropertiesData,
    isEqual
  );
  const completeCaseDetail = useSelector(
    (state) => state["caseDetailReducer"].completeCaseDetail,
    isEqual
  );
  useEffect(() => {
    if (Array.isArray(selectDocument) && selectDocument.length === 1) {
      const d = first(selectDocument);
      readExcelFromBucket(
        dispatch,
        d.s3ObjectName,
        d.documentName,
        d.originalFileName,
        setRows
      );
    }
  }, [selectDocument, dispatch]);

  const createGuidString = useCallback(() => new Guid().toString(), []);
  
  useEffect(() => {
    const validate = async () => {
      if (!Array.isArray(rows) || rows.length === 0) {
        dispatch({
          type: SET_UPLOADED_CHARGE_DATA,
          data: { uploadedChargeData: [] },
        });
        return;
      }

      const formattedData = rows.map((row) => {
        const tgtTypeKey = findKeyContaining(row, "target type");
        const tgtValueKey = findKeyContaining(row, "target value");
        const atRiskKey = findKeyContaining(row, "at risk");

        return {
          roundTargetType: safeString(row[tgtTypeKey]),
          roundTargetValue: safeString(row[tgtValueKey]),
          atRiskAmnt: safeString(row[atRiskKey]),
          fromServiceDate: new Date(
            completeCaseDetail?.statementFromDate ??
              auditPropertiesData?.serviceFromDate
          ),
          toServiceDate: new Date(
            completeCaseDetail?.statementToDate ??
              auditPropertiesData?.serviceToDate
          ),
        };
      });
      asyncAction.asyncOperationStart(
        VALIDATE_UPLOADED_CHARGE_DATA,
        formattedData
      );
    };

    validate();
  }, [rows, dispatch]);
  const saveUploadedRoundLineLevelCharges = useCallback(() => {
    dispatch({ type: SHOW_LOADER });

    setRoundCharges((prev) => {
      const newItems = (uploadedChargeData || [])
        .filter((row) => row.isValid === true)
        .map((row) => ({
          ...row,
          eaRoundId,
          eaRoundCaseLineLevelTrackingIdString: createGuidString(),
        }));

      const updated = [...(prev || []), ...newItems];

      const denialData = updated.filter(
        (item) => item.outcomeType === OutcomeTypes.DeniedWillAppeal
      );
      const obj =
        denialData.length > 0
          ? { roundOutcomeId: null, roundOutcomeType: "" }
          : {};

      if (caseData?.roundData && currentEaRoundCaseId !== undefined) {
        const updatedRoundData = caseData.roundData.map((round) =>
          round.eaRoundCaseId === currentEaRoundCaseId
            ? { ...round, ...obj, roundCharges: updated }
            : round
        );

        dispatch({
          type: SET_CASE_FORM_DATA,
          data: { caseData: { ...caseData, roundData: updatedRoundData } },
        });
      }

      return updated;
    });

    toggleUploadTargetDataPopup(dispatch, false);
    dispatch({ type: HIDE_LOADER });
  }, [
    dispatch,
    uploadedChargeData,
    eaRoundId,
    setRoundCharges,
    caseData,
    currentEaRoundCaseId,
    createGuidString,
  ]);

  const downloadValidatedExcel = useCallback(() => {
    dispatch({ type: SHOW_LOADER });

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Validated Charges");
    worksheet.columns = [
      { header: "Target Type", key: "roundTargetType", width: 20 },
      { header: "Target Value", key: "roundTargetValue", width: 20 },
      { header: "At Risk Dollar", key: "atRiskAmnt", width: 20 },
      { header: "Error Message", key: "error", width: 40 },
    ];

    (uploadedChargeData || []).forEach((row) => {
      worksheet.addRow({
        roundTargetType: row.roundTargetType ?? "",
        roundTargetValue: row.roundTargetValue ?? "",
        atRiskAmnt: row.atRiskAmnt ?? "",
        error: row.error ?? "",
      });
    });

    workbook.xlsx
      .writeBuffer()
      .then((buffer) =>
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "Validated_Round_Line_Level_Charges.xlsx"
        )
      )
      .finally(() => dispatch({ type: HIDE_LOADER }));
  }, [dispatch, uploadedChargeData]);

  const onTabSelectionChanged = useCallback(() => {
  setRows([]);
  setSelectedDocument([]);

  dispatch({
    type: SET_DOCUMENT_DATA_TO_SAVE,
    data: { selectedDocumentId: 0 },
  });
  dispatch({
    type: SET_UPLOADED_CHARGE_DATA,
    data: { uploadedChargeData: [] },
  });
  setFileResetKey(Date.now());
  refreshDocumentManagementGridInstance();
}, [dispatch]);

  const handleRowsLoaded = useCallback((newRows) => {
    setRows(newRows || []);
  }, []);

  const renderPopupTitle = useCallback(
    () => (
      <MdaePopupTitle
        closePopupHandler={() => toggleUploadTargetDataPopup(dispatch, false)}
      >
        Upload Line Level Charge Data
      </MdaePopupTitle>
    ),
    [dispatch]
  );

  const renderFooter = useCallback(
    () => (
      <MdaePopupFooter>
        <MdaePopupFooterStartButtons>
          <MdaeButton
            id={"pnlEditDocumentSecondaryBtn"}
            role={"secondary"}
            validationGroup="documentValidationGroup"
            onClick={(e) => {
              e.validationGroup?.reset();
              toggleUploadTargetDataPopup(dispatch, false);
            }}
          >
            Cancel
          </MdaeButton>
        </MdaePopupFooterStartButtons>
        <MdaePopupFooterEndButtons>
          {uploadedChargeData?.length > 0 && doesHaveAnyInValidRow && (
            <MdaeButton
              id={"pnlEditDocumentPrimaryBtn"}
              role={"primary"}
              validationGroup="documentValidationGroup"
              onClick={downloadValidatedExcel}
            >
              Download Validated Data
            </MdaeButton>
          )}
          <MdaeButton
            id={"pnlEditDocumentPrimaryBtn"}
            role={"primary"}
            validationGroup="documentValidationGroup"
            disabled={!(uploadedChargeData?.length > 0)}
            onClick={saveUploadedRoundLineLevelCharges}
          >
            {doesHaveAnyInValidRow ? "Save Valid Rows" : "Save"}
          </MdaeButton>
        </MdaePopupFooterEndButtons>
      </MdaePopupFooter>
    ),
    [
      dispatch,
      uploadedChargeData,
      doesHaveAnyInValidRow,
      downloadValidatedExcel,
      saveUploadedRoundLineLevelCharges,
    ]
  );
  const mdaeDataUpdaterConfig = useMemo(
    () => [
      {
        actionType: TOGGLE_UPLOAD_DOCUMENT_TARGET_CHARGES_POPUP,
        reducerName: "caseDataReducer",
        widgetOptionBindings: {
          visible: "isUploadTargetDataPopupVisible",
          mdaePopupTitle: "Upload Target Data",
        },
      },
    ],
    []
  );
  return (
    <>
      <MdaePopup
        deferRendering={false}
        height={700}
        width={990}
        visible={isVisible}
        mdaeTitleRender={renderPopupTitle}
        mdaeDataUpdaterConfig={mdaeDataUpdaterConfig}
        mdaeContentRender={() => (
          <div className="mdae-document-uploader-popup-content">
            <MdaePopupContent>
              <MdaeTabPanel onSelectionChanged={onTabSelectionChanged}>
                <Item title={"My Computer"}>
                  <div>
                    <EditOrAttachDocument onRowsLoaded={handleRowsLoaded} key={fileResetKey}/>
                    <TargetTypeDataGrid key ={"my-computer-datagrid"} />
                  </div>
                </Item>
                <Item title={"Document Management"}>
                  <div>
                    <DocumentManagement
                      hasAuditViewAccess={true}
                      isManagerView={false}
                      doNotLoadDatasourcesInd={true}
                      showForCoderReviewInd={false}
                      isBulkDwonloadVisible={false}
                      height={"350"}
                      attachedLevelId={eaCaseId}
                      attachedLevelName="EAC"
                      userActionMode={
                        USER_ACTION_MODE.VALIDATE_SINGLE_SELECTION
                      }
                      isFromCaseDetails={false}
                      setSelectedRowData={setSelectedDocument}
                      isDocumentUploadDisable={true}
                    />
                  </div>
                  <div>
                    Validated Line Level Charge Data:
                    <TargetTypeDataGrid key ={"document-management"} />
                  </div>
                </Item>
              </MdaeTabPanel>
            </MdaePopupContent>
          </div>
        )}
        mdaeFooterRender={renderFooter}
      />
      <WaitTimeMessageModal />
    </>
  );
};

export default UploadTargetDataPopup;
